import React from 'react'
import axios from 'axios'
import api from 'configs/api'
import { alertDanger, alertSuccess } from 'utils/toast'
import { translate } from 'locales'
import { IMedicalRecord } from './interfaces'
import { buildTagsDoctorPayload } from './psychologyRecordPayload'
import getAge from 'utils/getAge'

export const saveMedicalRecord = async (
  userUUID: string,
  scheduleUUID: string,
  data: IMedicalRecord,
  history: any,
  setLoading: React.Dispatch<React.SetStateAction<boolean>>,
  specialtySlug?: string,
  /**
   * Fase 5 — roda **depois** de o atendimento virar `finished`, e nunca pode
   * derrubar a finalização. Ver `encerrarConversaDoPar`.
   */
  aoFinalizar?: () => Promise<unknown>
) => {
  setLoading(true)

  if (data?.analysis && data?.analysis.length > 20000) {
    setLoading(false)
    return alertDanger(translate('errors.textMax'))
  }

  if (specialtySlug === 'psicologo') {
    const hasDoctorTags = data?.tagsDoctor && data.tagsDoctor.length > 0
    const hasPatientTags = data?.tagsPatient && data.tagsPatient.length > 0

    if (!hasDoctorTags && !hasPatientTags) {
      setLoading(false)
      return alertDanger(translate('errors.errorTags'))
    }
  }
  try {
    const tagsDoctorPayload = buildTagsDoctorPayload(data?.tagsDoctor)

    const save = await api.post('/doctors/ms/v1/medical-record/psicology', {
      patient_id: userUUID,
      scheduling_id: scheduleUUID,
      specialty_id: data.specialty,
      annotation: data.analysis,
      tags_doctor: tagsDoctorPayload,
      tags_patient: data?.tagsPatient,
      nutritional_anamnese: data?.nutritional_anamnese,
    })
    if (save.data) {
      await api.patch(`/schedulings/ms/v1/${scheduleUUID}/doctor/status`, {
        status: 'finished',
      })

      if (aoFinalizar) {
        try {
          await aoFinalizar()
        } catch (e) {
          console.warn('[MedicalRecord] pós-finalização falhou', e)
        }
      }

      setLoading(false)
      history.replace('/dashboard')
      window.localStorage.removeItem(`starbem:schedule:${scheduleUUID}`)
      window.localStorage.removeItem(`starbem:scheduling:${scheduleUUID}`)
      window.localStorage.removeItem(`clickTime${scheduleUUID}`)
      window.sessionStorage.removeItem(`starbem:schedule:${scheduleUUID}`)
      return alertSuccess(translate('success.schedulingFinished'))
    }
    setLoading(false)
    return alertDanger(
      save.data?.data?.errors[0].msg || translate('errors.errorTry')
    )
  } catch (err: any) {
    setLoading(false)
    return alertDanger(
      err?.response?.data?.data?.errors[0]?.msg ||
        translate('errors.internalError')
    )
  }
}

export const findAllUserPsicologyMedicalRecord = async (
  patient: any,
  specialty: string,
  isActive: boolean,
  setTimeline: React.Dispatch<React.SetStateAction<Array<any>>>,
  setLoading: React.Dispatch<React.SetStateAction<boolean>>,
  page?: number,
  setTotalPages?: any
) => {
  try {
    const { data } = await api.get(
      `/doctors/v2/medical-record/user/${patient.id}/psico/${specialty}?page=${page}&limit=6`
    )

    setTotalPages(data?.totalPages)
    setTimeline(data.medicalRecords)
    setLoading(false)
  } catch (err: any) {
    alertDanger(
      err?.response?.data?.data?.errors[0]?.msg ||
        translate('errors.internalError')
    )
    setLoading(false)
  }
}

export const saveNutritionalGuidance = async (
  scheduleUUID: string,
  patientId: string,
  nutritionalGuidance: string,
  setLoading: React.Dispatch<React.SetStateAction<boolean>>
) => {
  try {
    setLoading(true)
    await api.post(`doctors/v1/nutritional-guidance`, {
      appointment_id: scheduleUUID,
      patient_id: patientId,
      nutritional_guidance: nutritionalGuidance,
    })
    setLoading(false)
    return alertSuccess(translate('success.orientationSave'))
  } catch (e: any) {
    setLoading(false)
    return alertDanger(
      e?.response?.data.message || translate('errors.errorOrientationSave')
    )
  }
}

export const normalizeDietType = (diet: string): string => {
  if (!diet) return 'onivora'

  const normalized = diet
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()

  const dietMap: any = {
    onivora: 'onivora',
    onívora: 'onivora',
    vegetariana: 'vegetariana',
    vegana: 'vegana',
    vegan: 'vegana',
    ovolactovegetariana: 'ovolactovegetariana',
    ovolacto: 'ovolactovegetariana',
    outros: 'outros',
    outras: 'outros',
  }

  return dietMap[normalized] || normalized
}

export const normalizeMealType = (meal: string): string => {
  if (!meal) return ''

  const normalized = meal
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
    .replace(/\s+/g, '_')

  const mealMap: any = {
    cafe_manha: 'breakfast',
    'café da manhã': 'breakfast',
    'cafe da manha': 'breakfast',
    breakfast: 'breakfast',
    lanche_manha: 'morning_snack',
    'lanche da manhã': 'morning_snack',
    'lanche da manha': 'morning_snack',
    morning_snack: 'morning_snack',
    almoco: 'lunch',
    almoço: 'lunch',
    lunch: 'lunch',
    lanche_tarde: 'afternoon_snack',
    'lanche da tarde': 'afternoon_snack',
    afternoon_snack: 'afternoon_snack',
    jantar: 'dinner',
    dinner: 'dinner',
    ceia: 'supper',
    supper: 'supper',
  }

  return mealMap[normalized] || ''
}

export const checkPlanSentStatus = async (scheduleUUID: string) => {
  try {
    const response = await api.get(
      `/stela-meal/v1/meal-menu/appointment/${scheduleUUID}`
    )

    return {
      isSent: response.data && response.data !== null,
      data: response.data,
    }
  } catch {
    return {
      isSent: false,
      data: null,
    }
  }
}

export const sendMealPlan = async (
  menuData: any,
  patientId: string,
  doctorId: string,
  scheduleUUID: string,
  isMenuModified: boolean
) => {
  try {
    if (!menuData || !patientId || !doctorId || !scheduleUUID) {
      alertDanger(translate('medicalRecord.insufficientDataToSendPlan'))
      return { success: false }
    }

    const payload = {
      patientId: patientId,
      doctorId: doctorId,
      appointmentId: scheduleUUID,
      modified: isMenuModified,
      menu: menuData.menu,
      metadados: menuData.metadados,
      anamnese: menuData.caracteristicas_anamnese,
    }

    const response = await api.post('/stela-meal/v1/meal-menu', payload)

    if (response.data) {
      alertSuccess(translate('medicalRecord.foodPlanSentSuccessfully'))
      return { success: true, data: response.data }
    }

    return { success: false }
  } catch (error: any) {
    alertDanger(
      error?.response?.data?.message ||
        translate('medicalRecord.errorSendingFoodPlan')
    )
    return { success: false, error }
  }
}

export const generateMealPlanPDF = async (appointmentId: string) => {
  try {
    const response = await api.get(
      `/stela-meal/v1/meal-menu/appointment/${appointmentId}/pdf`,
      {
        responseType: 'blob',
      }
    )

    const url = window.URL.createObjectURL(new Blob([response.data]))
    const link = document.createElement('a')
    link.href = url
    link.setAttribute('download', `plano-alimentar-${appointmentId}.pdf`)
    document.body.appendChild(link)
    link.click()
    link.remove()
    window.URL.revokeObjectURL(url)

    alertSuccess(translate('medicalRecord.pdfGeneratedSuccessfully'))
    return { success: true }
  } catch (error: any) {
    alertDanger(
      error?.response?.data?.message ||
        translate('medicalRecord.errorGeneratingPDF')
    )
    return { success: false, error }
  }
}

interface RegenerateMealPlanParams {
  anamneseData: any
  userData: any
  userMedical: any
  appointment: any
  scheduleUUID: string
  doctorId?: string
  convertNewFormatToOld: (plan: any, anamneseData?: any) => any
  planId?: string
  meal?: string
}

export const regenerateMealPlan = async ({
  anamneseData,
  userData,
  userMedical,
  appointment,
  scheduleUUID,
  doctorId,
  convertNewFormatToOld,
  planId,
  meal,
}: RegenerateMealPlanParams) => {
  try {
    const token = window.localStorage.getItem('token')

    if (!token) {
      alertDanger('Token de autenticação não encontrado')
      return { success: false, error: 'Token não encontrado' }
    }

    if (!anamneseData) {
      alertDanger('Dados da anamnese não encontrados')
      return { success: false, error: 'Anamnese não encontrada' }
    }

    const age =
      parseInt(anamneseData?.age) || getAge(userData?.birthdate || '') || 0

    if (!age || age <= 0) {
      alertDanger('Idade é obrigatória')
      return { success: false, error: 'Idade obrigatória' }
    }

    if (!anamneseData?.current_weight) {
      alertDanger('Peso atual é obrigatório')
      return { success: false, error: 'Peso obrigatório' }
    }

    const heightM =
      anamneseData?.height_m ||
      (userMedical?.height ? Number(userMedical.height / 100).toFixed(2) : '')

    if (!heightM) {
      alertDanger('Altura é obrigatória')
      return { success: false, error: 'Altura obrigatória' }
    }

    if (!anamneseData?.diet) {
      alertDanger('Tipo de dieta é obrigatório')
      return { success: false, error: 'Dieta obrigatória' }
    }

    const genderMap: any = {
      male: 'M',
      female: 'F',
      other: 'O',
    }
    const gender = genderMap[userData?.gender] || 'M'

    const primaryObjectiveMap: any = {
      Emagrecimento: 'emagrecimento',
      Ganho: 'ganho_de_peso',
      'Ganho de peso': 'ganho_de_peso',
      'Ganho de massa': 'ganho_de_massa',
      'Manutenção de peso': 'manutencao',
      'Melhoria da composição corporal': 'melhoria_composicao',
      'Iniciar um hábito de alimentar mais saudável': 'habito_saudavel',
    }

    const primaryObjective = anamneseData?.reason?.[0]
      ? primaryObjectiveMap[anamneseData.reason[0]] || 'emagrecimento'
      : 'emagrecimento'

    const supplementsList = anamneseData?.supplements || []
    const supplementsFormatted = supplementsList.map((supp: string) => ({
      name: supp,
      notes: anamneseData?.supplements_comments || '',
    }))

    const allergies: string[] = []
    const intolerances: string[] = []

    if (anamneseData?.allergies === 'Sim' && anamneseData?.allergies_comments) {
      const allergiesText = anamneseData.allergies_comments
      const allergiesList = allergiesText
        .split(/[,\n]/)
        .map((item: string) => item.trim())
        .filter((item: string) => item.length > 0)
      allergies.push(...allergiesList)
    }

    if (
      anamneseData?.intolerances === 'Sim' &&
      anamneseData?.intolerances_comments
    ) {
      const intolerancesText = anamneseData.intolerances_comments
      const intolerancesList = intolerancesText
        .split(/[,\n]/)
        .map((item: string) => item.trim())
        .filter((item: string) => item.length > 0)
      intolerances.push(...intolerancesList)
    }

    const aversions: string[] = []
    if (anamneseData?.food_aversion) {
      const aversionsText = anamneseData.food_aversion
      const aversionsList = aversionsText
        .split(/[,\n]/)
        .map((item: string) => item.trim())
        .filter((item: string) => item.length > 0)
      aversions.push(...aversionsList)
    }

    const preferences: string[] = []
    if (anamneseData?.preferences) {
      const prefsText = anamneseData.preferences
      const prefsList = prefsText
        .split(/[,\n]/)
        .map((item: string) => item.trim())
        .filter((item: string) => item.length > 0)
      preferences.push(...prefsList)
    }

    const sleepText =
      anamneseData?.sleep?.join(', ') || anamneseData?.sleep_comments || ''

    const bowelHabits = {
      frequency: anamneseData?.frequency_intestinal_habit || '',
      consistency: anamneseData?.consistency_intestinal_habit || '',
    }

    const currentEatingRoutine = {
      routine_breakfast: anamneseData?.routine_breakfast || '',
      routine_morning_snack: anamneseData?.routine_morning_snack || '',
      routine_lunch: anamneseData?.routine_lunch || '',
      routine_afternoon_snack: anamneseData?.routine_afternoon_snack || '',
      routine_dinner: anamneseData?.routine_dinner || '',
      routine_supper: anamneseData?.routine_supper || '',
      routine_weekends: anamneseData?.routine_weekends || '',
    }

    const userStorage: any = window.localStorage.getItem('@StarBem:user')
    const doctorData = userStorage ? JSON.parse(userStorage) : null
    let userId = doctorData?.id || appointment?.doctor_id || doctorId

    if (userId) {
      userId = String(userId).trim()
    }

    if (
      !userId ||
      userId === '' ||
      userId === 'undefined' ||
      userId === 'null'
    ) {
      alertDanger('ID do médico não encontrado. Faça login novamente.')
      return { success: false, error: 'ID do médico não encontrado' }
    }

    const region =
      appointment?.user_country === 'BR' ? 'sudeste_br' : 'sudeste_br'

    const normalizedMeal = normalizeMealType(meal || '')

    if (!normalizedMeal && meal) {
      alertDanger(
        `Tipo de refeição inválido: ${meal}. Use: breakfast, morning_snack, lunch, afternoon_snack, dinner ou supper`
      )
      return { success: false, error: 'Tipo de refeição inválido' }
    }

    const apiPayload: any = {
      request_metadata: {
        patient_id: appointment?.user_id || scheduleUUID,
        doctor_id: userId,
        appointment_id: scheduleUUID,
        provider_id: process.env.REACT_APP_MEAL_PLAN_PROVIDER_ID || '',
        region: region,
        request_type: 'meal_builder',
        plan_id: planId || '',
        meal: normalizedMeal,
      },
      patient_profile: {
        full_name: userData?.name || userData?.social_name || '',
        gender: gender,
        age: age,
        current_weight_kg: parseFloat(anamneseData?.current_weight) || 0,
        old_weight_kg: parseFloat(anamneseData?.old_weight) || 0,
        height_m: parseFloat(heightM) || 0,
      },
      nutritional_plan_goals: {
        primary_objective: primaryObjective,
        reasons: anamneseData?.reason || [],
        flexibility_notes: anamneseData?.flexibility_notes || '',
        goal_notes: anamneseData?.reason_comments || '',
      },
      medical_and_supplements: {
        pathologies: anamneseData?.pathologies || [],
        medications: {
          uses: anamneseData?.medicines || false,
          which_ones: anamneseData?.medicines_comments
            ? [anamneseData.medicines_comments]
            : [],
        },
        supplements: supplementsFormatted,
      },
      dietary_restrictions_and_habits: {
        diet_type: normalizeDietType(anamneseData?.diet || ''),
        allergies: allergies,
        intolerances: intolerances,
        aversions: aversions,
        preferences: preferences,
        water_intake: anamneseData?.water_consumption || '',
        other_considerations:
          anamneseData?.considerations || anamneseData?.extra_information || '',
      },
      routine_and_activity: {
        physical_activity: {
          practices: anamneseData?.practice_activity || false,
          type: anamneseData?.practice_activity_type || '',
          frequency_schedule: anamneseData?.practice_activity_comments || '',
        },
        sleep: sleepText,
        bowel_habits: bowelHabits,
      },
      current_eating_routine: currentEatingRoutine,
    }

    if (
      !apiPayload.request_metadata.doctor_id ||
      apiPayload.request_metadata.doctor_id === ''
    ) {
      alertDanger('ID do médico não encontrado. Faça login novamente.')
      return { success: false, error: 'ID do médico inválido' }
    }

    const response = await axios.post(
      process.env.REACT_APP_MEAL_PLAN_BASE_URL || '',
      apiPayload,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
          'X-API-Key': process.env.REACT_APP_MEAL_PLAN_API_KEY,
        },
      }
    )

    let mealPlanData

    try {
      mealPlanData =
        typeof response.data === 'string'
          ? JSON.parse(response.data)
          : response.data
    } catch {
      throw new Error('Erro ao processar resposta da API: dados inválidos')
    }

    if (!mealPlanData) {
      throw new Error('Resposta vazia da API')
    }

    if (mealPlanData.errorMessage || mealPlanData.errorType) {
      const backendMsg = String(mealPlanData.errorMessage || '')
      if (backendMsg.includes('BEDROCK_MODEL_ID')) {
        alertDanger(
          'Falha na configuração do modelo de IA. Contate o suporte (BEDROCK_MODEL_ID ausente).'
        )
      } else {
        alertDanger(
          mealPlanData.errorMessage ||
            mealPlanData.error ||
            'Erro ao regenerar plano alimentar. Tente novamente.'
        )
      }
      return {
        success: false,
        error: mealPlanData.errorMessage || mealPlanData.error,
      }
    }

    if (mealPlanData.success === false) {
      let errorMsg =
        mealPlanData.message ||
        mealPlanData.error ||
        mealPlanData.errorMessage ||
        'Erro desconhecido'

      if (
        mealPlanData.validation_errors &&
        Array.isArray(mealPlanData.validation_errors)
      ) {
        const validationDetails = mealPlanData.validation_errors.join('; ')
        errorMsg = `${errorMsg}. Detalhes: ${validationDetails}`
      }

      throw new Error(errorMsg)
    }

    const converted = convertNewFormatToOld(
      mealPlanData.plan || mealPlanData,
      anamneseData
    )

    if (!converted) {
      throw new Error('Erro ao converter dados do plano')
    }

    return {
      success: true,
      data: converted,
      rawData: mealPlanData.plan || mealPlanData,
      metadata: mealPlanData.metadata || null,
    }
  } catch (error: any) {
    let errorMessage = 'Erro ao regenerar plano alimentar. Tente novamente.'
    if (error?.message) {
      errorMessage = error.message
    }
    alertDanger(errorMessage)
    return { success: false, error: errorMessage }
  }
}

// export const createAccompaniment = async (data: any) => {
//   try {
//     const response = await api.post('/doctors/v1/accompaniment', data)
//     return response.data
//   } catch (err: any) {
//     return alertDanger(
//       err.response?.data?.data?.message || translate('errors.internalError')
//     )
//   }
// }
